import { api, DomainWithAge, DomainFilters, PaginatedResponse } from '../api/client';

interface State {
  loading: boolean;
  error: string | null;
  data: DomainWithAge[];
  pagination: PaginatedResponse<DomainWithAge>['pagination'] | null;
  filters: DomainFilters;
}

const state: State = {
  loading: false,
  error: null,
  data: [],
  pagination: null,
  filters: {
    page: 1,
    limit: 50,
    sort: 'domain',
    order: 'asc',
  },
};

export async function renderDomainList(container: HTMLElement) {
  container.innerHTML = `
    <div class="card">
      <h2 class="card-header">Amass Marketplace</h2>

      <!-- Search Bar -->
      <div class="search-bar">
        <input
          type="text"
          id="search-input"
          class="form-input"
          placeholder="Search domains..."
          value="${state.filters.search || ''}"
        />
        <button id="search-btn" class="btn btn-primary">Search</button>
      </div>

      <!-- Filters -->
      <div class="filters">
        <div class="filter-group">
          <span class="filter-label">TLD</span>
          <select id="filter-tld" class="filter-select">
            <option value="">All TLDs</option>
            <option value="com" ${state.filters.tld === 'com' ? 'selected' : ''}>COM</option>
            <option value="net" ${state.filters.tld === 'net' ? 'selected' : ''}>NET</option>
            <option value="org" ${state.filters.tld === 'org' ? 'selected' : ''}>ORG</option>
            <option value="io" ${state.filters.tld === 'io' ? 'selected' : ''}>IO</option>
            <option value="co" ${state.filters.tld === 'co' ? 'selected' : ''}>CO</option>
          </select>
        </div>

        <div class="filter-group">
          <span class="filter-label">Type</span>
          <select id="filter-type" class="filter-select">
            <option value="">All Types</option>
            <option value="auction" ${state.filters.listing_type === 'auction' ? 'selected' : ''}>Auction</option>
            <option value="buy-now" ${state.filters.listing_type === 'buy-now' ? 'selected' : ''}>Buy Now</option>
          </select>
        </div>

        <div class="filter-group">
          <span class="filter-label">Min Age (years)</span>
          <input
            type="number"
            id="filter-min-age"
            class="filter-select"
            placeholder="Min"
            min="0"
            value="${state.filters.min_age || ''}"
          />
        </div>

        <div class="filter-group">
          <span class="filter-label">Max Price ($)</span>
          <input
            type="number"
            id="filter-max-price"
            class="filter-select"
            placeholder="Max"
            min="0"
            value="${state.filters.max_price || ''}"
          />
        </div>

        <div class="filter-group">
          <span class="filter-label">Sort By</span>
          <select id="filter-sort" class="filter-select">
            <option value="domain" ${state.filters.sort === 'domain' ? 'selected' : ''}>Domain Name</option>
            <option value="price" ${state.filters.sort === 'price' ? 'selected' : ''}>Price</option>
            <option value="age" ${state.filters.sort === 'age' ? 'selected' : ''}>Age</option>
            <option value="auction_end" ${state.filters.sort === 'auction_end' ? 'selected' : ''}>Auction End</option>
          </select>
        </div>

        <div class="filter-group">
          <span class="filter-label">Order</span>
          <select id="filter-order" class="filter-select">
            <option value="asc" ${state.filters.order === 'asc' ? 'selected' : ''}>Ascending</option>
            <option value="desc" ${state.filters.order === 'desc' ? 'selected' : ''}>Descending</option>
          </select>
        </div>
      </div>

      <!-- Results -->
      <div id="domain-results"></div>

      <!-- Pagination -->
      <div id="pagination" class="pagination"></div>
    </div>
  `;

  setupEventListeners(container);
  await loadDomains();
}

function setupEventListeners(container: HTMLElement) {
  // Search
  const searchBtn = container.querySelector('#search-btn') as HTMLButtonElement;
  const searchInput = container.querySelector('#search-input') as HTMLInputElement;

  searchBtn.addEventListener('click', () => {
    state.filters.search = searchInput.value;
    state.filters.page = 1;
    loadDomains();
  });

  searchInput.addEventListener('keypress', (e) => {
    if (e.key === 'Enter') {
      state.filters.search = searchInput.value;
      state.filters.page = 1;
      loadDomains();
    }
  });

  // Filters
  const filterTld = container.querySelector('#filter-tld') as HTMLSelectElement;
  const filterType = container.querySelector('#filter-type') as HTMLSelectElement;
  const filterMinAge = container.querySelector('#filter-min-age') as HTMLInputElement;
  const filterMaxPrice = container.querySelector('#filter-max-price') as HTMLInputElement;
  const filterSort = container.querySelector('#filter-sort') as HTMLSelectElement;
  const filterOrder = container.querySelector('#filter-order') as HTMLSelectElement;

  const applyFilters = () => {
    state.filters.tld = filterTld.value || undefined;
    state.filters.listing_type = filterType.value || undefined;
    state.filters.min_age = filterMinAge.value ? parseInt(filterMinAge.value) : undefined;
    state.filters.max_price = filterMaxPrice.value ? parseFloat(filterMaxPrice.value) : undefined;
    state.filters.sort = filterSort.value || undefined;
    state.filters.order = filterOrder.value as 'asc' | 'desc' || undefined;
    state.filters.page = 1;
    loadDomains();
  };

  filterTld.addEventListener('change', applyFilters);
  filterType.addEventListener('change', applyFilters);
  filterMinAge.addEventListener('change', applyFilters);
  filterMaxPrice.addEventListener('change', applyFilters);
  filterSort.addEventListener('change', applyFilters);
  filterOrder.addEventListener('change', applyFilters);
}

async function loadDomains() {
  const resultsDiv = document.getElementById('domain-results') as HTMLElement;
  const paginationDiv = document.getElementById('pagination') as HTMLElement;

  // Show loading
  resultsDiv.innerHTML = `
    <div class="loading">
      <div class="spinner"></div>
      <p>Loading domains...</p>
    </div>
  `;

  const response = await api.listDomains(state.filters);

  if (!response.success) {
    resultsDiv.innerHTML = `
      <div class="alert alert-error">
        ${response.error || 'Failed to load domains'}
      </div>
    `;
    return;
  }

  const { data, pagination } = response.data!;
  state.data = data;
  state.pagination = pagination;

  if (data.length === 0) {
    resultsDiv.innerHTML = `
      <div class="empty-state">
        <h3>No domains found</h3>
        <p>Try adjusting your search or filters</p>
      </div>
    `;
    paginationDiv.innerHTML = '';
    return;
  }

  // Render cards
  resultsDiv.innerHTML = `
    <div class="domain-grid">
      ${data.map((domain) => renderDomainCard(domain)).join('')}
    </div>
  `;

  // Render pagination
  renderPagination(paginationDiv, pagination);
}

function renderDomainCard(domain: DomainWithAge): string {
  const price = domain.current_price
    ? `$${domain.current_price.toLocaleString()}`
    : '-';

  const age = domain.age_years
    ? `${domain.age_years.toFixed(1)} yrs`
    : 'Unknown';

  const auctionEnd = domain.auction_end_date
    ? new Date(domain.auction_end_date).toLocaleDateString()
    : '-';

  const typeClass = domain.listing_type.replace('-', '');

  const namecheapUrl = `https://www.namecheap.com/market/buynow/?query=${encodeURIComponent(domain.domain_name)}`;

  return `
    <article class="domain-card">
      <div class="domain-card__top">
        <div class="domain-card__title">
          <span class="domain-name">${escapeHtml(domain.domain_name.split('.')[0])}</span><span class="domain-tld">.${domain.tld}</span>
        </div>
        <span class="listing-type ${typeClass}">${domain.listing_type}</span>
      </div>
      <div class="domain-card__price">
        <span class="price-label">Price</span>
        <span class="price-value">${price}</span>
      </div>
      <div class="domain-card__meta">
        <div>
          <span class="meta-label">Age</span>
          <span class="meta-value">${age}</span>
        </div>
        <div>
          <span class="meta-label">Auction</span>
          <span class="meta-value">${auctionEnd}</span>
        </div>
      </div>
      <div class="domain-card__actions">
        <a href="${namecheapUrl}" target="_blank" rel="noopener" class="btn btn-secondary">
          View
        </a>
      </div>
    </article>
  `;
}

function renderPagination(
  container: HTMLElement,
  pagination: PaginatedResponse<DomainWithAge>['pagination']
) {
  const { page, totalPages, total } = pagination;

  if (totalPages <= 1) {
    container.innerHTML = `<span class="pagination-info">${total} domain${total !== 1 ? 's' : ''} found</span>`;
    return;
  }

  container.innerHTML = `
    <button id="page-prev" ${page === 1 ? 'disabled' : ''}>Previous</button>
    <span class="pagination-info">
      Page ${page} of ${totalPages} (${total} domains)
    </span>
    <button id="page-next" ${page === totalPages ? 'disabled' : ''}>Next</button>
  `;

  document.getElementById('page-prev')?.addEventListener('click', () => {
    if (state.filters.page && state.filters.page > 1) {
      state.filters.page--;
      loadDomains();
    }
  });

  document.getElementById('page-next')?.addEventListener('click', () => {
    if (state.filters.page && state.filters.page < totalPages) {
      state.filters.page++;
      loadDomains();
    }
  });
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
